import type { ApolloQueryResult } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GET_EVENTS_CONNECTION } from "./queries";
import type { IEventAttr, IEventData } from "./types";
import { formatEvents } from "./utils";

import type {
  EventsSortInput,
  GetEventsConnectionQuery,
  GetEventsConnectionQueryVariables,
} from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

interface IUseEventsQuery {
  events: IEventData[];
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<GetEventsConnectionQuery>>;
  fetchMore: () => Promise<void>;
  hasNextPage: boolean;
  total: number;
}

const useEventsQuery = (
  groupName: string,
  filters: GetEventsConnectionQueryVariables["filters"],
  sortBy: EventsSortInput[],
): IUseEventsQuery => {
  const { addAuditEvent } = useAudit();
  const { t } = useTranslation();

  const { data, loading, refetch, fetchMore } = useQuery(
    GET_EVENTS_CONNECTION,
    {
      onCompleted: (): void => {
        addAuditEvent("Group.Events", groupName);
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          Logger.warning("An error occurred loading group data", error);
          msgError(t("groupAlerts.errorTextsad"));
        });
      },
      variables: { filters, first: 100, groupName, sortBy },
    },
  );

  const pageInfo = data?.group.eventsConnection.pageInfo ?? {
    endCursor: "",
    hasNextPage: false,
  };

  const events = useMemo(
    (): IEventData[] =>
      formatEvents(
        data?.group.eventsConnection.edges.map(
          (edge): IEventAttr => edge.node,
        ) ?? [],
      ),
    [data],
  );

  return {
    events,
    fetchMore: async (): Promise<void> => {
      await fetchMore({ variables: { after: pageInfo.endCursor } });
    },
    hasNextPage: pageInfo.hasNextPage,
    loading,
    refetch,
    total: data?.group.eventsConnection.total ?? 0,
  };
};

export { useEventsQuery };
